<script lang="ts">
interface Props {
    customerCompanyId?: uuid
    tableId?: string
    fetchEndpoint?: string
    entityName?: string
    downloading?: boolean
}
</script>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { computed, onBeforeMount, ref, watch } from 'vue'
import { RouteLocationRaw, useRoute } from 'vue-router'
import dayjs from 'dayjs'

import { useCompanyStore } from '@/stores/company-store'
import { useAuthStore } from '@/stores/auth-store'
import {
    uuid,
    PaginatedResponse,
    MinimalResource,
    ResourceResponse,
    MyDateRangePickerRef,
} from '@/types/general'
import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { ShipmentType, MinimalShipment, Tour } from '@/types/delivery-management'
import { GetDataParameters } from '@/types/table'
import { datetime, localeDate, localeTime } from '@/utils/dates'
import { DropdownStringOption } from '@/types/inputs'
import { useLazyLoadedList } from '@/hooks/use-lazy-loaded-list'
import { useMittListener } from '@/hooks/use-mitt-listener'
import { shipmentTypes } from '@/utils/delivery-management'
import { LicenseType } from '@/types/company'

import ShipmentStatus from '@/components/delivery-management/ShipmentStatus.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import ActionMenuItem from '@/components/table/ActionMenuItem.vue'
import MyFilterButton from '@/components/table/MyFilterButton.vue'
import ManageShipmentModal from '@/components/delivery-management/ManageShipmentModal.vue'
import HandleShipmentModal from '@/components/delivery-management/HandleShipmentModal.vue'
import MyFilterDivider from '@/components/table/MyFilterDivider.vue'
import MyFilterSelect from '@/components/table/MyFilterSelect.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'
import CreateTicketModal from '@/components/delivery-management/CreateTicketModal.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'
import ConsignmentNoteModal from '@/components/e-cmr/ConsignmentNoteModal.vue'
import BasicExportModal from '@/components/import-export/BasicExportModal.vue'

interface FetchShipmentsParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        type: ShipmentType | undefined
        status: string | undefined
        'hub-ids': uuid | string | undefined
        'tour-id': uuid | undefined
        'sending-location-id': uuid | undefined
        'customer-company-id': uuid | undefined
        'start-date': string | undefined
        'end-date': string | undefined
    }
}

const props = defineProps<Props>()
const companyStore = useCompanyStore()
const { t } = useI18n()
const authStore = useAuthStore()
const route = useRoute()

const { data, paginationData, params, loading, error, refetch } = usePaginatedTable<
    MinimalShipment,
    FetchShipmentsParams
>(
    async (params, abortController) => {
        const response = await axios.get<PaginatedResponse<MinimalShipment>>(
            window.route(fetchEndpoint.value, {
                company: authStore.companyId,
            }),
            { params: params, signal: abortController.signal },
        )
        return response.data
    },
    {
        filter: {
            type: undefined,
            status: undefined,
            'hub-ids': undefined,
            'tour-id': undefined,
            'sending-location-id': undefined,
            'start-date': dayjs().subtract(6, 'days').startOf('day').toDate().toISOString(),
            'end-date': dayjs().endOf('day').toDate().toISOString(),
            'customer-company-id': props.customerCompanyId,
        },
    },
)
const hubs = useLazyLoadedList<DropdownStringOption>(fetchHubs)

const tour = ref<Tour>()
const shipmentModalOpen = ref(false)
const handleShipmentModalOpen = ref(false)
const cmrModalOpen = ref(false)
const selectedShipment = ref<MinimalShipment>()
const ticketModalOpen = ref(false)
const dateRangePickerRef = ref<MyDateRangePickerRef>()

const shipmentStatuses = computed<DropdownStringOption[]>(() => [
    { value: 'unassigned', label: t('noAssignedTour') },
    { value: 'notDeliveredOrPickedUp', label: t('notDeliveredOrPickedUp') },
    { value: 'deliveredOrPickedUp', label: t('deliveredOrPickedUp') },
    { value: 'delayed', label: t('delayed') },
    { value: 'early', label: t('early') },
    { value: 'inTransit', label: t('inTransit') },
])

const locations = computed<DropdownStringOption[]>(() =>
    companyStore.locations.map(({ id, name }) => ({ value: id, label: name })),
)
const fetchEndpoint = computed(() => props.fetchEndpoint ?? 'dm.company.shipments.index')
const entityName = computed(() => props.entityName ?? 'shipments')
const tableId = computed(() => props.tableId ?? 'dm-shipments')

const exportEndpoint = computed(() => {
    return window.route('dm.company.shipments.export', {
        company: authStore.companyId,
        ...route.query,
    })
})

async function fetchTour(id?: uuid) {
    try {
        const response = await axios.get<ResourceResponse<Tour>>(
            window.route('dm.company.tours.show', {
                company: authStore.companyId,
                tour: id,
            }),
        )
        tour.value = response.data.data
    } finally {
        loading.value = false
    }
}

async function fetchHubs(): Promise<DropdownStringOption[]> {
    const response = await axios.get<MinimalResource[]>(
        window.route('minimal.companies.hubs', { company: authStore.companyId }),
    )

    return response.data.map((hub) => ({
        value: hub.id,
        label: hub.name,
    }))
}

function isSenderOrShipper(shipment: MinimalShipment) {
    if (shipment.sendingCompany.id === authStore.companyId) return true
    if (shipment.shipperCompany?.id === authStore.companyId) return true
    return false
}

function updateShipment(shipment: MinimalShipment) {
    selectedShipment.value = shipment
    shipmentModalOpen.value = true
}

function handleShipment(shipment: MinimalShipment) {
    selectedShipment.value = shipment
    handleShipmentModalOpen.value = true
}

function createTicket(shipment: MinimalShipment) {
    selectedShipment.value = shipment
    ticketModalOpen.value = true
}

function showCMR(shipment: MinimalShipment) {
    selectedShipment.value = shipment
    cmrModalOpen.value = true
}

async function shipmentModalClosed() {
    shipmentModalOpen.value = false
    selectedShipment.value = undefined
}

async function handleShipmentModalClosed() {
    handleShipmentModalOpen.value = false
    selectedShipment.value = undefined
}

async function cmrModalClosed() {
    cmrModalOpen.value = false
    selectedShipment.value = undefined
}

function setFilter(type: string | number | null | undefined, filterName: string) {
    params.value.filter[filterName] = type
    params.value.page = 1
}

function resetTour() {
    params.value.filter['tour-id'] = undefined
    tour.value = undefined
}

function showShipment(id: string): RouteLocationRaw {
    if (route.name?.toString()?.includes('customers.show')) {
        return {
            name: 'dm.customers.shipments.show',
            params: { ...route.params, shipmentId: id },
            query: route.query,
        }
    }

    return {
        name: 'dm.shipments.show',
        params: { shipmentId: id },
        query: route.query,
    }
}

onBeforeMount(() => {
    if (params.value.filter['hub-ids']) hubs.fetch()
})

watch(
    () => [params.value.filter['tour-id']],
    () => {
        if (!params.value.filter['tour-id']) {
            tour.value = undefined
        } else {
            fetchTour(params.value.filter['tour-id'])
        }
    },
)

useMittListener('fetchShipments', () => refetch())
</script>

<template>
    <div v-if="props.customerCompanyId" class="flex items-center justify-end py-4 px-4">
        <BasicExportModal
            :title="t('exportModel', { model: t('shipments') })"
            :endpoint="exportEndpoint"
        />
    </div>

    <MyTable
        :table-id="tableId"
        :rows="data"
        :pagination-data="paginationData"
        :loading="loading || props.downloading"
        :error="error"
        :get-data="refetch"
        :entity-name="entityName"
        :has-time-range="!!params.filter['start-date']"
    >
        <template #filters>
            <MyDateRangePicker
                ref="dateRangePickerRef"
                :filter="params.filter"
                @start-date-changed="setFilter($event, 'start-date')"
                @end-date-changed="setFilter($event, 'end-date')"
            />

            <MyFilterDivider />

            <MyFilterButton
                filter-name="type"
                :value="null"
                default
                @selected="setFilter"
                v-text="t('all')"
            />

            <MyFilterButton
                filter-name="type"
                :value="ShipmentType.Delivery.toString()"
                @selected="setFilter"
                v-text="t('delivery')"
            />

            <MyFilterButton
                filter-name="type"
                :value="ShipmentType.Return.toString()"
                @selected="setFilter"
                v-text="t('return')"
            />

            <MyFilterButton
                filter-name="type"
                :value="ShipmentType.Pickup.toString()"
                @selected="setFilter"
                v-text="t('pickup')"
            />

            <MyFilterDivider />

            <MyFilterButton
                v-if="tour"
                class="!bg-primary-200 dark:!bg-dark-400"
                :value="null"
                filter-name="tour-id"
                @selected="resetTour"
                v-text="tour.name"
            />

            <MyFilterSelect
                :label="t('hubs')"
                :options="hubs.items.value"
                :loading="hubs.fetching.value"
                filter-name="hub-ids"
                translation-key="hubsSelected"
                multiple
                @focus="hubs.fetch()"
                @change="setFilter"
            />

            <MyFilterSelect
                :label="t('locations')"
                :options="locations"
                filter-name="sending-location-id"
                @change="setFilter"
            />

            <MyFilterDivider />

            <MyFilterSelect
                :label="t('status')"
                :options="shipmentStatuses"
                :dropdown-width="240"
                filter-name="status"
                translation-key="statusesSelected"
                multiple
                @change="setFilter"
            />
        </template>

        <template #actionRow="{ row }">
            <slot name="customActionRow" :row="row">
                <RouterLink :to="showShipment(row.id)">
                    <ActionRowItem>
                        <mdi:eye />
                    </ActionRowItem>
                </RouterLink>
            </slot>
        </template>
        <template v-if="!authStore.isReadonly.deliveryManagement" #actionMenu="{ row }">
            <slot name="customActionMenu" :row="row">
                <ActionMenuItem
                    v-if="authStore.hasLicense(LicenseType.ECMRModule)"
                    @click="showCMR(row)"
                >
                    <span class="mr-2"><mdi:document /></span>
                    {{ t('showCmr') }}
                </ActionMenuItem>

                <ActionMenuItem
                    v-if="row.shipperCompany && isSenderOrShipper(row)"
                    @click="createTicket(row)"
                >
                    <span class="mr-2"><mdi:briefcase-edit /></span>
                    {{ t('createEntity', { entity: t('ticket') }) }}
                </ActionMenuItem>

                <ActionMenuItem v-if="isSenderOrShipper(row)" @click="updateShipment(row)">
                    <span class="mr-2"><mdi:pencil /></span>
                    {{ t('edit') }}
                </ActionMenuItem>

                <ActionMenuItem
                    v-if="!row.handledAt && isSenderOrShipper(row)"
                    @click="handleShipment(row)"
                >
                    <mdi:package-variant-closed-check class="mr-2" />
                    {{
                        row.type === ShipmentType.Delivery
                            ? t('markAsDelivered')
                            : t('markAsPickedUp')
                    }}
                </ActionMenuItem>
            </slot>
        </template>

        <MyTableColumn :name="t('shipmentNumber')" property="shipmentNumber" filterable />
        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('name')"
            property="name"
            filterable
        />
        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('timeslot')"
            property="timeslot"
        />
        <template #timeslot="{ row }">
            <div>
                {{ row.handleableAfter ? localeTime(row.handleableAfter) : null }}
                <span v-if="row.handleableAfter && row.handleableBefore" v-text="'-'" />
                {{ row.handleableBefore ? localeTime(row.handleableBefore) : null }}
            </div>
        </template>

        <MyTableColumn :name="t('receiver')" property="receivingCompany.name" filterable />
        <template #[`receivingCompany.name`]="{ row }">
            <RouterLink
                v-if="row.receivingCustomerId"
                :to="{
                    name: 'dm.customers.show',
                    params: { id: row.receivingCustomerId },
                }"
            >
                <div class="flex">
                    {{ row.receivingCompany.name }}
                    <div v-if="row.soldTo" class="ml-1">
                        <span v-text="'-'" />
                        {{ row.soldTo }}
                    </div>
                </div>
            </RouterLink>
            <div v-else class="flex">
                {{ row.receivingCompany.name }}
                <div v-if="row.soldTo" class="ml-1">
                    <span v-text="'-'" />
                    {{ row.soldTo }}
                </div>
            </div>
        </template>
        <MyTableColumn :name="t('receiverLocation')" property="receivingLocation.name" filterable />
        <template #[`receivingLocation.name`]="{ row }">
            <div class="flex">
                {{ row.receivingLocation.name }}
                <div v-if="row.shipTo" class="ml-1">
                    <span v-text="'-'" />
                    {{ row.shipTo }}
                </div>
            </div>
        </template>
        <MyTableColumn :name="t('receiverAddress')" property="address" />
        <template #address="{ row }">
            {{ row.address ?? row.receivingLocation.address }}
        </template>
        <MyTableColumn
            :name="t('receiving', { property: t('zipcode') })"
            property="receivingLocation.zipcode"
            hidden
        />

        <MyTableColumn
            :name="t('receiving', { property: t('city') })"
            property="receivingLocation.city"
            hidden
        />

        <MyTableColumn
            :name="t('receiving', { property: t('country') })"
            property="receivingLocation.country"
            hidden
        />

        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('tour')"
            property="tour.name"
            :sortable="false"
        />
        <MyTableColumn :name="t('plannedAt')" property="plannedAt" />
        <template #plannedAt="{ row }">{{ localeDate(row.plannedAt) }}</template>
        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('status')"
            property="status"
            :sortable="false"
        />
        <template #status="{ row }">
            <div class="flex items-center">
                <ShipmentStatus class="truncate" :shipment="row" />
            </div>
        </template>
        <MyTableColumn :name="t('handledAt')" property="handledAt" />
        <template #handledAt="{ row }">
            {{ row.handledAt ? datetime(row.handledAt) : null }}
        </template>
        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('deviations')"
            property="deviationsCount"
        />
        <template #deviationsCount="{ row }">
            <div class="flex items-center space-x-2">
                <span v-text="row.deviationsCount" />
                <mdi:alert v-if="row.deviationsCount > 0" class="text-yellow-500" />
            </div>
        </template>
        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('tickets')"
            property="ticketsCount"
        />
        <template #ticketsCount="{ row }">
            <div class="flex items-center space-x-2">
                <span v-text="row.ticketsCount" />
                <mdi:alert v-if="row.ticketsCount > 0" class="text-yellow-500" />
            </div>
        </template>

        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('collis')"
            property="collisCount"
            filterable
        />
        <template #collisCount="{ row }">
            {{ row.collisCount }}
        </template>

        <MyTableColumn
            :name="t('sendingLocation')"
            property="sendingLocation.name"
            hidden
            filterable
        />
        <MyTableColumn :name="t('sender')" property="sendingCompany.name" hidden filterable />
        <MyTableColumn
            :name="t('shippingLocation')"
            property="shipperLocation.name"
            hidden
            filterable
        />
        <MyTableColumn :name="t('shipper')" property="shipperCompany.name" hidden filterable />
        <MyTableColumn
            :name="t('haulierLocation')"
            property="haulierLocation.name"
            hidden
            filterable
        />
        <MyTableColumn :name="t('haulier')" property="haulierCompany.name" hidden filterable />
        <MyTableColumn :name="t('type')" property="type" hidden />
        <template #type="{ row }">
            {{ t(shipmentTypes[row.type]) }}
        </template>
        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('transactions')"
            property="transactions"
            :sortable="false"
        />
        <template #transactions="{ row }">
            <div class="space-x-1 flex">
                <div v-for="transaction in row.transactions" :key="transaction.id">
                    <RouterLink
                        v-tooltip="
                            t('packagingTransaction') + '\n' + datetime(transaction.confirmedAt)
                        "
                        :to="{
                            name: 'packaging.transactions.show',
                            params: { transactionId: transaction.id },
                        }"
                    >
                        <mdi:package-variant-closed class="h-4 w-4" />
                    </RouterLink>
                </div>
            </div>
        </template>

        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('category')"
            property="category.name"
            hidden
        />
        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('deliveredBy')"
            property="deliveredByUser.name"
            hidden
            filterable
        />
        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('deliveryNote')"
            property="deliveryNote"
            hidden
        />
        <MyTableColumn
            v-if="entityName !== 'consignmentNote'"
            :name="t('references')"
            property="references"
            hidden
            filterable
        />
        <MyTableColumn :name="t('receiverName')" property="receiverName" hidden />
        <template #noDataButton>
            <MyButton
                v-if="params.filter['start-date']"
                plain
                scheme="light"
                size="small"
                @click="dateRangePickerRef?.increaseRange(6, 'months')"
            >
                <mdi:magnify class="mr-2 h-4 w-4" />
                {{ t('searchLast6Months') }}
            </MyButton>
        </template>
    </MyTable>

    <ManageShipmentModal
        v-model="shipmentModalOpen"
        :shipment-id="selectedShipment?.id"
        @close="shipmentModalClosed"
        @saved="refetch()"
    />

    <HandleShipmentModal
        v-if="selectedShipment"
        v-model="handleShipmentModalOpen"
        :shipment-id="selectedShipment.id"
        :shipment-type="selectedShipment.type"
        @close="handleShipmentModalClosed"
        @saved="refetch()"
    />

    <CreateTicketModal v-model="ticketModalOpen" :shipment="selectedShipment" />

    <ConsignmentNoteModal
        v-if="selectedShipment"
        :id="selectedShipment.id"
        v-model:open="cmrModalOpen"
        @close="cmrModalClosed"
    />
</template>
